var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @desc polar model
 */
import * as lodash from '../../utils/lodash';
import { Group, Sector, Polygon } from '../../element';
import { AngleAxis, RadiusAxis } from '../axis';
import CoordPolar from '../../coord/polar/index';
import { ComponentModel, renderModel } from '../../model';
import { getPercentOrNumberValue, degToRad, isPercent } from '../../utils/math';
import normalizeToArray from '../../utils/normalizeToArray';
/**
 * polar model
 */
var PolarModel = /** @class */ (function (_super) {
    __extends(PolarModel, _super);
    function PolarModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'polar';
        _this._sectorClip = new Sector({ fill: '#ffffff' });
        _this._polygonClip = new Polygon({ fill: '#ffffff' });
        _this._topLevelElements = [];
        return _this;
    }
    PolarModel.prevProcessOption = function (option) {
        option.polar = normalizeToArray(option.polar || {});
        option.series = option.series.map(function (seriesOption) {
            return lodash.assign({
                polar: 0,
            }, seriesOption);
        });
    };
    PolarModel.prototype.getDefaultOption = function () {
        return {
            center: ['50%', '50%'],
            size: '75%',
            innerSize: 0,
            startAngle: -90,
            endAngle: null,
            shape: 'auto',
            background: 'none',
            facet: 0,
        };
    };
    PolarModel.prototype.getInitialState = function () {
        return {
            cx: 0,
            cy: 0,
            radius: 0,
            innerRadius: 0,
            startAngle: 0,
            endAngle: 0,
        };
    };
    PolarModel.prototype.init = function () {
        var _this = this;
        this.initAxisModels();
        var onEvent = function (event) {
            return _this.handleUIEvent(event, 'plot', {
                target: _this,
            });
        };
        this._sectorClip.setAttr({ onEvent: onEvent });
        this._polygonClip.setAttr({ onEvent: onEvent });
        var renderer = this.chart.getRenderInstance();
        renderer.addEventElement(this._sectorClip);
        renderer.addEventElement(this._polygonClip);
    };
    PolarModel.prototype.initAxisModels = function () {
        var _this = this;
        var option = this.chart.getOption();
        var angleAxis = option.angleAxis, radiusAxis = option.radiusAxis;
        var polarAngleAxis = angleAxis.filter(function (axisOption) { return (axisOption.polar || 0) === _this.extraInfo.polarIndex; })[0];
        var polarRadiusAxis = radiusAxis.filter(function (axisOption) { return (axisOption.polar || 0) === _this.extraInfo.polarIndex; })[0];
        this.angleAxis = new AngleAxis('angleAxis', polarAngleAxis, this.chart, { axis: 'angleAxis', index: angleAxis.indexOf(polarAngleAxis) }, this);
        this.radiusAxis = new RadiusAxis('radiusAxis', polarRadiusAxis, this.chart, { axis: 'radiusAxis', index: radiusAxis.indexOf(polarRadiusAxis) }, this);
        option.angleAxis[this.angleAxis.extraInfo.index] = this.angleAxis.getOption();
        option.radiusAxis[this.radiusAxis.extraInfo.index] = this.radiusAxis.getOption();
        this.angleAxis.init();
        this.radiusAxis.init();
    };
    PolarModel.prototype.getCoordinate = function () {
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius, innerRadius = _a.innerRadius;
        return new CoordPolar(this.angleAxis, this.radiusAxis, cx, cy, radius, innerRadius);
    };
    PolarModel.prototype.getAllAxis = function () {
        return [this.angleAxis, this.radiusAxis];
    };
    PolarModel.prototype.getDependentSeries = function () {
        var _this = this;
        return this.chart
            .getSeriesByDependency('coord')
            .filter(function (seriesModel) {
            return (seriesModel.option.coord === 'polar' &&
                seriesModel.option.polar === _this.extraInfo.polarIndex);
        });
    };
    PolarModel.prototype.beforeRender = function () {
        var center = this.option.center;
        var _a = this.chart.getFacetContentBox(this.option.facet), top = _a.top, left = _a.left, width = _a.width, height = _a.height;
        var radius = getPercentOrNumberValue(this.option.size, Math.min(width, height)) / 2;
        var innerRadius = getPercentOrNumberValue(this.option.innerSize, radius * 2) / 2;
        var startAngle = this.option.startAngle;
        var hasDepend = this.getDependentSeries().length > 0;
        var endAngle = lodash.isNumber(this.option.endAngle)
            ? this.option.endAngle
            : this.option.startAngle + 360;
        if (Math.abs(endAngle - startAngle) >= 360) {
            endAngle = endAngle > startAngle ? startAngle + 360 : startAngle - 360;
        }
        var cx = (isPercent(center[0]) ? left + getPercentOrNumberValue(center[0], width) : center[0]);
        var cy = (isPercent(center[1]) ? top + getPercentOrNumberValue(center[1], height) : center[1]);
        this.setState({
            cx: cx,
            cy: cy,
            radius: radius,
            innerRadius: innerRadius,
            startAngle: startAngle,
            endAngle: endAngle,
        });
        var usePolygonShape = this.option.shape === 'polygon';
        this._sectorClip.setAttr({ display: !usePolygonShape && hasDepend });
        this._polygonClip.setAttr({ display: usePolygonShape && hasDepend });
        if (!usePolygonShape) {
            this._sectorClip.setAttr({
                cx: cx,
                cy: cy,
                radius: radius,
                radiusI: innerRadius,
                start: degToRad(startAngle),
                end: degToRad(endAngle),
                lineWidth: 0,
            });
        }
        this.angleAxis.beforeRender();
        this.radiusAxis.beforeRender();
        if (usePolygonShape) {
            var points = this.radiusAxis.getPolygonPointsOnRadius(radius);
            this._polygonClip.setAttr({
                pointList: points,
            });
        }
    };
    PolarModel.prototype.afterRender = function () {
        this.angleAxis.afterRender();
        this.radiusAxis.afterRender();
        this._topLevelElements.length = 0;
    };
    PolarModel.prototype.getClipPath = function () {
        return this.option.shape === 'polygon' ? this._polygonClip : this._sectorClip;
    };
    PolarModel.prototype.addToPolarTop = function (item) {
        this._topLevelElements.push(item);
    };
    PolarModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('polar'),
        });
        if (this.getDependentSeries().length === 0) {
            return;
        }
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius, innerRadius = _a.innerRadius, startAngle = _a.startAngle, endAngle = _a.endAngle;
        var background = this.option.background;
        var polarBackground = new Sector({
            key: this.getKey('polar-background'),
            cx: cx,
            cy: cy,
            radius: radius,
            radiusI: innerRadius,
            start: degToRad(startAngle),
            end: degToRad(endAngle),
            fill: background,
            stroke: null,
            pointerEvents: 'none',
        });
        group.add(polarBackground);
        group.add(renderModel(this.radiusAxis));
        group.add(renderModel(this.angleAxis));
        this._topLevelElements.forEach(function (item) { return group.add(item); });
        return group;
    };
    PolarModel.prototype.beforeDestroy = function () {
        var renderer = this.chart.getRenderInstance();
        renderer.removeEventElement(this._sectorClip);
        renderer.removeEventElement(this._polygonClip);
        if (this.angleAxis && this.radiusAxis) {
            this.angleAxis.destroy();
            this.radiusAxis.destroy();
        }
    };
    return PolarModel;
}(ComponentModel));
export default PolarModel;
